import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Eye, Heart, Download, Search, Filter, Calendar, DollarSign, User, Receipt, ArrowRight, Trash2, Plus, Check, Printer, Edit, Package, Mail, ClipboardList, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { QuotePreviewModal } from '../QuotePreviewModal';
import QuoteOrderProgress from '../QuoteOrderProgress';
import { formatPrice } from '../../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { PermissionGate } from '../../context/PermissionContext';
import { roomGroupedRowsHtml } from '../../lib/cartGroups';
import { openInstallSheet } from '../../lib/installSheet';
import { LeadSourceChip } from './ConsultationSource';
import { NeedsPricingBadge, needsPricing } from './NeedsPricingBadge';
import { RequestTrail } from './RequestTrail';
import { UnlinkedRequestsCounts } from './UnlinkedRequestsCounts';
import { HelpLink } from './HelpLink';
import { takeAdminParam } from '../../lib/adminLinks';
import { useAuth } from '../../context/AuthContext';
import { useTaxPreview, TaxTotals, taxLabel, isFullAdmin } from '../tax/taxDisplay';
import { TaxControls, TaxReadOnly } from '../tax/TaxControls';
import { DepositControls } from '../payments/DepositControls';
import { TaxExplainLine, TestBadge, NexusBanner } from '../tax/TaxBadges';
import { DeliveryAddressFields } from '../tax/DeliveryAddressFields';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const QUOTE_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  { value: 'awaiting_approval', label: 'Sent to Client', color: 'bg-blue-100 text-blue-700' },
  { value: 'changes_requested', label: 'Changes Requested', color: 'bg-purple-100 text-purple-700' },
  { value: 'client_approved', label: 'Client Approved', color: 'bg-teal-100 text-teal-700' },
  { value: 'signed', label: 'Signed', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'approved', label: 'Approved (Admin)', color: 'bg-green-100 text-green-700' },
  { value: 'production', label: 'Production', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'scheduled', label: 'Scheduled for Install', color: 'bg-violet-100 text-violet-700' },
  { value: 'completed', label: 'Completed', color: 'bg-neutral-100 text-neutral-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
  { value: 'converted_to_sales_order', label: 'Converted to Sales Order', color: 'bg-green-100 text-green-700' },
  { value: 'converted_to_invoice', label: 'Converted to Invoice', color: 'bg-green-100 text-green-700' },
];

export const QuotesTab = ({ token }) => {
  const { user: authUser } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => (takeAdminParam('filter') === 'needs_pricing' ? 'needs_pricing' : 'all'));
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [creating, setCreating] = useState(false);
  const [clients, setClients] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('new');
  const [newQuote, setNewQuote] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    company_name: '',
    billing_address: { street: '', city: '', state: '', zip: '' },
    shipping_address: { street: '', city: '', state: '', zip: '' },
    items: [{ description: '', width: '', height: '', quantity: 1, unit_price: 0, total: 0 }],
    notes: '',
    subtotal: 0,
    tax: 0,
    total: 0,
    assigned_to: ''
  });
  const newQuoteTax = useTaxPreview({ subtotal: newQuote.subtotal, destination: newQuote.shipping_address, customerEmail: newQuote.customer_email, token });
  const editQuoteTax = useTaxPreview({ subtotal: editingQuote?.subtotal, destination: editingQuote?.shipping_address, customerEmail: editingQuote?.customer_email, quoteId: editingQuote?.id, token, refreshKey: editingQuote?.tax_history?.length });

  useEffect(() => {
    fetchQuotes();
    fetchClients();
    fetchSalesReps();
  }, []);

  // Check for customer data from Customer page "New Transaction" button
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');
    
    if (action === 'create') {
      // Check localStorage for customer data
      const storedCustomer = localStorage.getItem('newQuoteCustomer');
      if (storedCustomer) {
        try {
          const customerData = JSON.parse(storedCustomer);
          setNewQuote(prev => ({
            ...prev,
            customer_name: customerData.name || '',
            customer_email: customerData.email || '',
            customer_phone: customerData.phone || '',
            company_name: customerData.company || '',
            billing_address: customerData.billing_address || prev.billing_address,
            shipping_address: customerData.shipping_address || prev.shipping_address,
          }));
          setSelectedClientId('new'); // Don't pre-select from clients dropdown
          localStorage.removeItem('newQuoteCustomer'); // Clear after using
          toast.success(`Creating quote for ${customerData.name}`);
        } catch (e) {
          console.error('Failed to parse customer data:', e);
        }
      }
      setShowCreateModal(true);
      // Clean URL
      window.history.replaceState({}, '', '/admin?tab=quotes');
    }
  }, []);

  const fetchClients = async () => {
    try {
      // Fetch from the new Customers CRM database
      const response = await axios.get(`${API_URL}/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Map customer data to match expected client format
      const customers = Array.isArray(response.data) ? response.data : [];
      const mappedClients = customers.map(customer => ({
        id: customer.id,
        name: customer.display_name || customer.primary_contact,
        email: customer.email,
        phone: customer.work_phone || customer.mobile_phone,
        company: customer.company_name,
        is_trade: customer.is_trade,
        customer_number: customer.customer_number,
        billing_address: customer.billing_address,
        shipping_address: customer.shipping_address,
        tax_preference: customer.tax_preference,
        tax_rate: customer.tax_rate,
        payment_terms: customer.payment_terms,
        discount_tier_id: customer.discount_tier_id
      }));
      setClients(mappedClients);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchSalesReps = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/company-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const salesRoles = ['role_sales_rep', 'role_sales_manager', 'role_admin', 'role_super_admin'];
      const reps = response.data.filter(user => 
        user.is_active !== false && 
        (salesRoles.includes(user.role_id) || user.is_admin)
      );
      setSalesReps(reps);
    } catch (error) {
      console.error('Failed to fetch sales reps:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
      const openId = new URLSearchParams(window.location.search).get('quote');
      const target = openId && response.data.find((q) => q.id === openId);
      if (target) {
        setSelectedQuote(target);
        const params = new URLSearchParams(window.location.search);
        params.delete('quote');
        window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params}`);
      }
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (quoteId) => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${quoteId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `apollo-quote-${quoteId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleSendQuoteEmail = async (quoteId, customerEmail) => {
    try {
      await axios.post(`${API_URL}/admin/quotes/${quoteId}/send-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Quote emailed to ${customerEmail}`);
      fetchQuotes(); // Refresh to show email sent status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quote email');
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await axios.put(`${API_URL}/admin/quotes/${quoteId}/status`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, status } : q));
      toast.success('Quote status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const convertToInvoice = async (quoteId) => {
    if (!window.confirm('Convert this quote to an invoice? This will mark the quote as approved.')) return;
    
    try {
      const response = await axios.post(`${API_URL}/admin/quotes/${quoteId}/convert-to-invoice`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invoice ${response.data.invoice_number} created!`);
      // Update local state
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, status: 'approved', invoice_id: response.data.invoice_id } : q));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const convertToSalesOrder = async (quoteId) => {
    if (!window.confirm('Convert this quote to a Sales Order? The customer will need to approve the SO.')) return;
    
    try {
      const response = await axios.post(`${API_URL}/admin/quotes/${quoteId}/convert-to-sales-order`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sales Order ${response.data.so_number} created!`);
      // Update local state
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, status: 'converted_to_so', sales_order_id: response.data.sales_order_id } : q));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Sales Order');
    }
  };

  const isTradeAccount = async (email) => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const user = response.data.find(u => u.email === email);
      return user?.is_trade || false;
    } catch {
      return false;
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(quotes.filter(q => q.id !== quoteId));
      toast.success('Quote deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quote');
    }
  };

  const addQuoteItem = () => {
    setNewQuote({
      ...newQuote,
      items: [...newQuote.items, { description: '', width: '', height: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const removeQuoteItem = (index) => {
    const items = newQuote.items.filter((_, i) => i !== index);
    updateQuoteTotals(items);
  };

  const updateQuoteItem = (index, field, value) => {
    const items = [...newQuote.items];
    items[index] = { ...items[index], [field]: value };
    
    // Calculate item total
    if (field === 'unit_price' || field === 'quantity') {
      items[index].total = (items[index].unit_price || 0) * (items[index].quantity || 1);
    }
    
    updateQuoteTotals(items);
  };

  const updateQuoteTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const tax = 0; // shown via /tax/preview, saved by the server
    setNewQuote({
      ...newQuote,
      items,
      subtotal,
      tax,
      total: subtotal + tax
    });
  };

  const createQuote = async () => {
    if (!newQuote.customer_name || !newQuote.customer_email) {
      toast.error('Please enter customer name and email');
      return;
    }
    if (newQuote.items.length === 0 || newQuote.items.every(i => !i.description)) {
      toast.error('Please add at least one item');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/admin/quotes`, newQuote, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes([response.data, ...quotes]);
      toast.success('Quote created successfully!');
      setShowCreateModal(false);
      resetQuoteForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quote');
    } finally {
      setCreating(false);
    }
  };

  const resetQuoteForm = () => {
    setNewQuote({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      company_name: '',
      billing_address: { street: '', city: '', state: '', zip: '' },
      shipping_address: { street: '', city: '', state: '', zip: '' },
      items: [{ description: '', width: '', height: '', quantity: 1, unit_price: 0, total: 0 }],
      notes: '',
      subtotal: 0,
      tax: 0,
      total: 0
    });
    setSelectedClientId('new');
  };

  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
    if (clientId === 'new') {
      resetQuoteForm();
    } else {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setNewQuote(prev => ({
          ...prev,
          customer_name: client.name || '',
          customer_email: client.email || '',
          customer_phone: client.phone || '',
          company_name: client.company || '',
          billing_address: client.billing_address || prev.billing_address,
          shipping_address: client.shipping_address || prev.shipping_address,
        }));
      }
    }
  };

  const openEditModal = (quote) => {
    // Convert quote items to edit format
    const editItems = quote.items?.map(item => ({
      id: item.id,
      description: item.config?.room_label || item.config?.product_type || '',
      width: item.config?.width || '',
      height: item.config?.height || '',
      quantity: item.quantity || 1,
      unit_price: item.config?.total_price ? item.config.total_price / (item.quantity || 1) : 0,
      total: item.config?.total_price || 0
    })) || [{ description: '', width: '', height: '', quantity: 1, unit_price: 0, total: 0 }];

    setEditingQuote({
      ...quote,
      items: editItems
    });
    setShowEditModal(true);
  };

  const updateEditItem = (index, field, value) => {
    const items = [...editingQuote.items];
    items[index] = { ...items[index], [field]: value };
    
    if (field === 'unit_price' || field === 'quantity') {
      items[index].total = (items[index].unit_price || 0) * (items[index].quantity || 1);
    }
    
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const tax = editingQuote.tax || 0; // server recalculates on save
    
    setEditingQuote({
      ...editingQuote,
      items,
      subtotal,
      tax,
      total: subtotal + tax
    });
  };

  const addEditItem = () => {
    setEditingQuote({
      ...editingQuote,
      items: [...editingQuote.items, { description: '', width: '', height: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const removeEditItem = (index) => {
    const items = editingQuote.items.filter((_, i) => i !== index);
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const tax = editingQuote.tax || 0; // server recalculates on save
    setEditingQuote({
      ...editingQuote,
      items,
      subtotal,
      tax,
      total: subtotal + tax
    });
  };

  const saveQuoteEdit = async () => {
    if (!editingQuote.customer_name || !editingQuote.customer_email) {
      toast.error('Please enter customer name and email');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.put(`${API_URL}/admin/quotes/${editingQuote.id}`, editingQuote, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(quotes.map(q => q.id === editingQuote.id ? response.data : q));
      toast.success('Quote updated successfully!');
      setShowEditModal(false);
      setEditingQuote(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update quote');
    } finally {
      setCreating(false);
    }
  };


  const emailQuoteDocument = async (quoteId, docType, email) => {
    if (!email) { toast.error('No customer email on this quote'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/quotes/${quoteId}/email-document`,
        { doc_type: docType, to_email: email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${docType === 'packing_slip' ? 'Packing Slip' : 'Install Sheet'} sent to ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handlePrint = (quote) => {
    // Create print-friendly version
    const printWindow = window.open('', '_blank');
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Quote #${quote.id?.slice(0, 8).toUpperCase()}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .header h1 { margin: 0; font-size: 28px; }
          .header p { margin: 5px 0; color: #666; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
          .info-box h3 { margin: 0 0 10px 0; font-size: 14px; color: #666; text-transform: uppercase; }
          .info-box p { margin: 3px 0; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background: #f5f5f5; font-weight: 600; }
          .totals { text-align: right; }
          .totals table { width: 300px; margin-left: auto; }
          .totals td { border: none; padding: 5px 10px; }
          .total-row { font-weight: bold; font-size: 18px; border-top: 2px solid #333 !important; }
          .footer { margin-top: 50px; text-align: center; color: #666; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>APOLLO SHADES</h1>
          <p>1 Brooklyn Navy Yard, Building 33, Suite 201B, Brooklyn, NY 11205</p>
          <p>(212) 209-0654 | info@apolloshades.com</p>
        </div>
        
        <h2 style="text-align: center;">QUOTE #${quote.id?.slice(0, 8).toUpperCase()}</h2>
        <p style="text-align: center; color: #666;">Date: ${new Date(quote.created_at).toLocaleDateString()}</p>
        
        <div class="info-grid">
          <div class="info-box">
            <h3>Bill To</h3>
            <p><strong>${quote.customer_name}</strong></p>
            ${quote.company_name ? `<p>${quote.company_name}</p>` : ''}
            <p>${quote.customer_email}</p>
            ${quote.customer_phone ? `<p>${quote.customer_phone}</p>` : ''}
          </div>
          <div class="info-box">
            <h3>Quote Details</h3>
            <p><strong>Status:</strong> ${quote.status?.charAt(0).toUpperCase() + quote.status?.slice(1)}</p>
            <p><strong>Items:</strong> ${quote.items?.length || 0}</p>
            ${quote.signature ? `<p><strong>Signed by:</strong> ${quote.signature.signer_name}</p>` : ''}
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Dimensions</th>
              <th>Qty</th>
              <th style="text-align: right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${roomGroupedRowsHtml(quote.items)}
          </tbody>
        </table>
        
        <div class="totals">
          <table>
            <tr><td>Subtotal</td><td>$${(quote.subtotal || 0).toFixed(2)}</td></tr>
            <tr><td>${taxLabel(quote)}</td><td>${quote.tax_details?.status === 'pending_address' ? 'TBC' : '$' + (quote.tax || 0).toFixed(2)}</td></tr>
            <tr class="total-row"><td>Total</td><td>$${(quote.total || 0).toFixed(2)}</td></tr>
          </table>
        </div>
        
        ${quote.notes ? `<div style="margin-top: 30px; padding: 15px; background: #f9f9f9; border-radius: 5px;"><strong>Notes:</strong> ${quote.notes}</div>` : ''}
        
        <div class="footer">
          <p>This quote is valid for 30 days. Thank you for choosing Apollo Shades!</p>
        </div>
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
  };

  const filteredQuotes = quotes.filter(quote => {
    if (statusFilter === 'needs_pricing' && !needsPricing(quote)) return false;
    if (statusFilter !== 'all' && statusFilter !== 'needs_pricing' && quote.status !== statusFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        quote.customer_name?.toLowerCase().includes(search) ||
        quote.customer_email?.toLowerCase().includes(search) ||
        quote.company_name?.toLowerCase().includes(search) ||
        quote.id?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const statusCounts = {
    all: quotes.length,
    pending: quotes.filter(q => q.status === 'pending').length,
    approved: quotes.filter(q => q.status === 'approved').length,
    production: quotes.filter(q => q.status === 'production').length,
    scheduled: quotes.filter(q => q.status === 'scheduled').length,
    completed: quotes.filter(q => q.status === 'completed').length,
  };

  const getStatusStyle = (status) => {
    const statusObj = QUOTE_STATUSES.find(s => s.value === status);
    return statusObj?.color || 'bg-neutral-100 text-neutral-700';
  };

  const getStatusLabel = (status) => {
    const statusObj = QUOTE_STATUSES.find(s => s.value === status);
    return statusObj?.label || status;
  };

  const totalValue = quotes.reduce((sum, q) => sum + (q.total || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">Customer Quotes</h3>
          <p className="text-sm text-neutral-500">
            {quotes.length} quotes • {formatPrice(totalValue)} total value
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGate module="quotes" level="full">
          <Button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-1" /> New Quote
          </Button>
          </PermissionGate>
          <Button onClick={fetchQuotes} variant="outline" size="sm">
            Refresh
          </Button>
        </div>
      </div>

      <UnlinkedRequestsCounts token={token} testId="quotes-unlinked-requests" />

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
          <div className="text-2xl font-bold text-neutral-900">{quotes.length}</div>
          <div className="text-sm text-neutral-500">Total Quotes</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
          <div className="text-2xl font-bold text-amber-600">{statusCounts.pending}</div>
          <div className="text-sm text-amber-700">Pending</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="text-2xl font-bold text-green-600">{statusCounts.approved}</div>
          <div className="text-sm text-green-700">Approved</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="text-2xl font-bold text-blue-600">{formatPrice(totalValue)}</div>
          <div className="text-sm text-blue-700">Total Value</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search quotes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'approved', 'production', 'scheduled', 'completed'].map(status => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status)}
              className={statusFilter === status ? getStatusStyle(status).replace('bg-', 'bg-').replace('text-', '') : ''}
            >
              {status === 'all' ? 'All' : getStatusLabel(status)} ({statusCounts[status] || 0})
            </Button>
          ))}
          <Button
            variant={statusFilter === 'needs_pricing' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('needs_pricing')}
            className={statusFilter === 'needs_pricing' ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'border-orange-200 text-orange-800'}
            data-testid="quotes-filter-needs-pricing"
          >
            <span>Needs Pricing (<span data-testid="quotes-needs-pricing-count">{quotes.filter(needsPricing).length}</span>)</span>
          </Button>
          <HelpLink topic="needs-pricing" />
        </div>
      </div>

      {/* Quotes List */}
      <div className="space-y-3">
        {filteredQuotes.length === 0 ? (
          <div className="text-center py-12 text-neutral-500">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No quotes found</p>
          </div>
        ) : (
          filteredQuotes.map((quote) => (
            <motion.div
              key={quote.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`border rounded-lg p-4 bg-white hover:border-amber-300 transition-colors ${
                quote.status === 'changes_requested' ? 'border-purple-300 bg-purple-50/30' :
                quote.status === 'signed' ? 'border-emerald-300 bg-emerald-50/30' :
                'border-neutral-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <FileText className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-medium">#{quote.id?.slice(0, 8).toUpperCase()}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusStyle(quote.status)}`}>
                        {getStatusLabel(quote.status)}
                      </span>
                      {quote.invoice_id && (
                        <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-blue-100 text-blue-700 flex items-center gap-1">
                          <Receipt className="w-3 h-3" /> Invoiced
                        </span>
                      )}
                      {quote.signature && (
                        <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-emerald-100 text-emerald-700 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Signed
                        </span>
                      )}
                      <NeedsPricingBadge quote={quote} />
                      {quote.general_request && (
                        <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-sky-100 text-sky-800" data-testid={`general-request-badge-${quote.id}`}>General request</span>
                      )}
                      {quote.source === 'preview_share' && (
                        <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-amber-100 text-amber-800 flex items-center gap-1" data-testid={`quote-from-preview-${quote.id}`}>
                          <Eye className="w-3 h-3" /> From room previews{quote.needs_pricing ? ' · needs sizing' : ''}
                        </span>
                      )}
                      {quote.source === 'fabric_shortlist' && (
                        <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-rose-100 text-rose-800 flex items-center gap-1" data-testid={`quote-from-favorites-${quote.id}`}>
                          <Heart className="w-3 h-3" /> From favorites · needs sizing
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-600">
                      {quote.customer_name} {quote.company_name && `• ${quote.company_name}`}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {quote.customer_email} • {new Date(quote.created_at).toLocaleDateString()}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2"><TestBadge doc={quote} testId={`quote-test-badge-${quote.id}`} /><TaxExplainLine doc={quote} testId={`quote-tax-explain-${quote.id}`} /></div>
                    {quote.quote_channel && <LeadSourceChip c={quote} hideIfMissing testPrefix="quote-source" />}
                    {quote.quote_request_number && <p className="text-xs text-neutral-500 mt-1" data-testid={`quote-from-request-${quote.id}`}>From quote request {quote.quote_request_number}</p>}
                    
                    {/* Show assigned sales rep */}
                    {quote.assigned_to && (
                      <p className="text-xs text-blue-500 flex items-center gap-1 mt-1">
                        <User className="w-3 h-3" />
                        {salesReps.find(r => r.id === quote.assigned_to)?.name || 
                         salesReps.find(r => r.id === quote.assigned_to)?.email || 
                         'Assigned'}
                      </p>
                    )}
                    
                    {/* Show signature info */}
                    {quote.signature && (
                      <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded inline-block">
                        Signed by {quote.signature.signer_name} on {new Date(quote.signature.signed_at).toLocaleDateString()}
                      </div>
                    )}
                    
                    {/* Show change request info */}
                    {quote.change_requests?.length > 0 && (
                      <div className="mt-2 text-xs text-purple-700 bg-purple-50 px-2 py-1 rounded max-w-md">
                        <strong>Change Request:</strong> {quote.change_requests[quote.change_requests.length - 1].message.slice(0, 100)}
                        {quote.change_requests[quote.change_requests.length - 1].message.length > 100 && '...'}
                      </div>
                    )}
                    
                    {/* Compact Order Progress */}
                    <QuoteOrderProgress 
                      quote={quote}
                      compact={true}
                      expandable={true}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-mono font-medium text-lg">{formatPrice(quote.total)}</p>
                    <p className="text-xs text-neutral-500">{quote.items?.length || 0} items</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={quote.status}
                      onChange={(e) => updateQuoteStatus(quote.id, e.target.value)}
                      className="text-sm border border-neutral-200 rounded px-2 py-1"
                    >
                      {QUOTE_STATUSES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    {!quote.invoice_id && !quote.sales_order_id && (
                      <>
                        {/* Show Create SO button for client-approved quotes or trade accounts */}
                        {(quote.status === 'client_approved' || quote.status === 'signed' || clients.find(c => c.email === quote.customer_email)?.is_trade) ? (
                          <Button
                            size="sm"
                            onClick={() => convertToSalesOrder(quote.id)}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-3"
                            title="Convert to Sales Order"
                            data-testid="create-so-btn"
                          >
                            <Package className="w-4 h-4 mr-1" /> Create SO
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => convertToInvoice(quote.id)}
                            className="bg-green-600 hover:bg-green-700 text-white font-medium px-3"
                            title="Convert to Invoice"
                          >
                            <Receipt className="w-4 h-4 mr-1" /> Invoice
                          </Button>
                        )}
                      </>
                    )}
                    {quote.sales_order_id && !quote.invoice_id && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium flex items-center gap-1">
                        <Package className="w-3 h-3" /> SO Pending Approval
                      </span>
                    )}
                    {quote.invoice_id && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium flex items-center gap-1">
                        <Receipt className="w-3 h-3" /> Invoiced
                      </span>
                    )}
                    <PermissionGate module="quotes" level="edit">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(quote)}
                      title="Edit Quote"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    </PermissionGate>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedQuote(quote)}
                      title="View Quote"
                      data-testid={`view-quote-btn-${quote.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrint(quote)}
                      title="Print Quote"
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openInstallSheet(quote, 'Quote')}
                      title="Print Install Sheet"
                      data-testid={`quote-install-sheet-${quote.id}`}
                    >
                      <ClipboardList className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => emailQuoteDocument(quote.id, 'install_sheet', quote.customer_email || quote.email)}
                      title="Email Install Sheet to Customer"
                      data-testid={`quote-email-install-${quote.id}`}
                      className="text-amber-600 hover:text-amber-700 hover:border-amber-300"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendQuoteEmail(quote.id, quote.customer_email)}
                      title={quote.email_sent ? "Resend Quote Email" : "Send Quote Email"}
                      className={quote.email_sent ? "border-green-300 text-green-600" : ""}
                    >
                      <Mail className="w-4 h-4" />
                      {quote.email_sent && <Check className="w-3 h-3 ml-1 text-green-500" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadPDF(quote.id)}
                      title="Download PDF"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <PermissionGate module="quotes" level="full">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteQuote(quote.id)}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      title="Delete Quote"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    </PermissionGate>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Quote Preview Modal */}
      <AnimatePresence>
        {selectedQuote && (
          <QuotePreviewModal
            quote={selectedQuote}
            onClose={() => setSelectedQuote(null)}
            onDownload={() => handleDownloadPDF(selectedQuote.id)}
            headerBadge={<NeedsPricingBadge quote={selectedQuote} testId="quote-detail-needs-pricing" />}
            extra={<RequestTrail quoteId={selectedQuote.id} token={token} />}
          />
        )}
      </AnimatePresence>

      {/* Create Quote Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <h2 className="text-lg font-semibold">Create New Quote</h2>
                  <p className="text-sm text-neutral-500">Create a quote for a client</p>
                </div>
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-neutral-100 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Customer Selector */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <label className="text-sm font-semibold text-amber-800 mb-2 block">Select Customer</label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => handleClientSelect(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="new">+ Add New Customer</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.customer_number} - {client.name}{client.company ? ` (${client.company})` : ''}{client.is_trade ? ' [Trade]' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Customer Information */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-700 mb-3">Customer Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Name *</label>
                      <Input
                        value={newQuote.customer_name}
                        onChange={(e) => setNewQuote({...newQuote, customer_name: e.target.value})}
                        placeholder="Customer name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Email *</label>
                      <Input
                        type="email"
                        value={newQuote.customer_email}
                        onChange={(e) => setNewQuote({...newQuote, customer_email: e.target.value})}
                        placeholder="customer@email.com"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Phone</label>
                      <Input
                        value={newQuote.customer_phone}
                        onChange={(e) => setNewQuote({...newQuote, customer_phone: e.target.value})}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Company</label>
                      <Input
                        value={newQuote.company_name}
                        onChange={(e) => setNewQuote({...newQuote, company_name: e.target.value})}
                        placeholder="Company name"
                      />
                    </div>
                  </div>
                </div>

                {/* Billing Address */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-700 mb-3">Billing Address</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="col-span-4">
                      <Input
                        value={newQuote.billing_address.street}
                        onChange={(e) => setNewQuote({...newQuote, billing_address: {...newQuote.billing_address, street: e.target.value}})}
                        placeholder="Street address"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        value={newQuote.billing_address.city}
                        onChange={(e) => setNewQuote({...newQuote, billing_address: {...newQuote.billing_address, city: e.target.value}})}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <Input
                        value={newQuote.billing_address.state}
                        onChange={(e) => setNewQuote({...newQuote, billing_address: {...newQuote.billing_address, state: e.target.value}})}
                        placeholder="State"
                      />
                    </div>
                    <div>
                      <Input
                        value={newQuote.billing_address.zip}
                        onChange={(e) => setNewQuote({...newQuote, billing_address: {...newQuote.billing_address, zip: e.target.value}})}
                        placeholder="ZIP"
                      />
                    </div>
                  </div>
                </div>

                <DeliveryAddressFields value={newQuote.shipping_address} onChange={(a) => setNewQuote({ ...newQuote, shipping_address: a })} testId="new-quote-delivery" />

                {/* Quote Items */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-neutral-700">Line Items</h3>
                    <Button variant="outline" size="sm" onClick={addQuoteItem}>
                      <Plus className="w-4 h-4 mr-1" /> Add Item
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {newQuote.items.map((item, index) => (
                      <div key={item.id || `quote-item-${index}`} className="grid grid-cols-12 gap-2 items-center p-3 bg-neutral-50 rounded-lg">
                        <div className="col-span-4">
                          <Input
                            value={item.description}
                            onChange={(e) => updateQuoteItem(index, 'description', e.target.value)}
                            placeholder="Description (e.g., Roller Shade - Bedroom)"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            value={item.width}
                            onChange={(e) => updateQuoteItem(index, 'width', e.target.value)}
                            placeholder='Width"'
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            value={item.height}
                            onChange={(e) => updateQuoteItem(index, 'height', e.target.value)}
                            placeholder='Height"'
                          />
                        </div>
                        <div className="col-span-1">
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateQuoteItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            placeholder="Qty"
                            min="1"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => updateQuoteItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            placeholder="Price"
                            step="0.01"
                          />
                        </div>
                        <div className="col-span-1 text-right">
                          {newQuote.items.length > 1 && (
                            <button
                              onClick={() => removeQuoteItem(index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium">Notes</label>
                  <textarea
                    value={newQuote.notes}
                    onChange={(e) => setNewQuote({...newQuote, notes: e.target.value})}
                    placeholder="Additional notes for the quote..."
                    className="mt-1 w-full px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    rows={3}
                  />
                </div>

                {/* Assigned Sales Rep */}
                <PermissionGate module="user_management" level="view" fallback={null}>
                <div>
                  <label className="text-sm font-medium">Assigned Sales Rep</label>
                  <select
                    value={newQuote.assigned_to || ''}
                    onChange={(e) => setNewQuote({...newQuote, assigned_to: e.target.value || null})}
                    className="mt-1 w-full px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    data-testid="quote-assigned-to-select"
                  >
                    <option value="">Unassigned</option>
                    {salesReps.map(rep => (
                      <option key={rep.id} value={rep.id}>
                        {rep.name || rep.email} {rep.role_id && `(${rep.role_id.replace('role_', '').replace(/_/g, ' ')})`}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-neutral-500 mt-1">Assign this quote to a sales representative</p>
                </div>
                </PermissionGate>

                {/* Totals */}
                <div className="bg-neutral-50 rounded-lg p-4">
                  <div className="flex justify-end space-y-1">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Subtotal</span>
                        <span className="font-mono">{formatPrice(newQuote.subtotal)}</span>
                      </div>
                      <TaxTotals subtotal={newQuote.subtotal} preview={newQuoteTax} formatPrice={formatPrice} testId="new-quote-tax" />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={createQuote} disabled={creating} className="btn-primary">
                    {creating ? 'Creating...' : 'Create Quote'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Quote Modal */}
      <AnimatePresence>
        {showEditModal && editingQuote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <h2 className="text-lg font-semibold">Edit Quote #{editingQuote.id?.slice(0, 8).toUpperCase()}</h2>
                  <p className="text-sm text-neutral-500">Update quote details</p>
                </div>
                <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-neutral-100 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Customer Information */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-700 mb-3">Customer Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Name *</label>
                      <Input
                        value={editingQuote.customer_name || ''}
                        onChange={(e) => setEditingQuote({...editingQuote, customer_name: e.target.value})}
                        placeholder="Customer name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Email *</label>
                      <Input
                        type="email"
                        value={editingQuote.customer_email || ''}
                        onChange={(e) => setEditingQuote({...editingQuote, customer_email: e.target.value})}
                        placeholder="customer@email.com"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Phone</label>
                      <Input
                        value={editingQuote.customer_phone || ''}
                        onChange={(e) => setEditingQuote({...editingQuote, customer_phone: e.target.value})}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Company</label>
                      <Input
                        value={editingQuote.company_name || ''}
                        onChange={(e) => setEditingQuote({...editingQuote, company_name: e.target.value})}
                        placeholder="Company name"
                      />
                    </div>
                  </div>
                </div>

                <DeliveryAddressFields value={editingQuote.shipping_address && typeof editingQuote.shipping_address === 'object' ? editingQuote.shipping_address : {}}
                  onChange={(a) => setEditingQuote({ ...editingQuote, shipping_address: a })} testId="edit-quote-delivery" />

                {/* Quote Items */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-neutral-700">Line Items</h3>
                    <Button variant="outline" size="sm" onClick={addEditItem}>
                      <Plus className="w-4 h-4 mr-1" /> Add Item
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {editingQuote.items?.map((item, index) => (
                      <div key={item.id || `edit-item-${index}`} className="grid grid-cols-12 gap-2 items-center p-3 bg-neutral-50 rounded-lg">
                        <div className="col-span-4">
                          <Input
                            value={item.description || ''}
                            onChange={(e) => updateEditItem(index, 'description', e.target.value)}
                            placeholder="Description"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            value={item.width || ''}
                            onChange={(e) => updateEditItem(index, 'width', e.target.value)}
                            placeholder='Width"'
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            value={item.height || ''}
                            onChange={(e) => updateEditItem(index, 'height', e.target.value)}
                            placeholder='Height"'
                          />
                        </div>
                        <div className="col-span-1">
                          <Input
                            type="number"
                            value={item.quantity || 1}
                            onChange={(e) => updateEditItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            placeholder="Qty"
                            min="1"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            value={item.unit_price || 0}
                            onChange={(e) => updateEditItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            placeholder="Price"
                            step="0.01"
                          />
                        </div>
                        <div className="col-span-1 text-right">
                          {editingQuote.items.length > 1 && (
                            <button
                              onClick={() => removeEditItem(index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium">Notes</label>
                  <textarea
                    value={editingQuote.notes || ''}
                    onChange={(e) => setEditingQuote({...editingQuote, notes: e.target.value})}
                    placeholder="Additional notes..."
                    className="mt-1 w-full px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    rows={3}
                  />
                </div>

                {/* Assigned Sales Rep */}
                <PermissionGate module="user_management" level="view" fallback={null}>
                <div>
                  <label className="text-sm font-medium">Assigned Sales Rep</label>
                  <select
                    value={editingQuote.assigned_to || ''}
                    onChange={(e) => setEditingQuote({...editingQuote, assigned_to: e.target.value || null})}
                    className="mt-1 w-full px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    data-testid="quote-edit-assigned-to-select"
                  >
                    <option value="">Unassigned</option>
                    {salesReps.map(rep => (
                      <option key={rep.id} value={rep.id}>
                        {rep.name || rep.email} {rep.role_id && `(${rep.role_id.replace('role_', '').replace(/_/g, ' ')})`}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-neutral-500 mt-1">Assign this quote to a sales representative</p>
                </div>
                </PermissionGate>

                {editingQuote.id && <TestBadge doc={editingQuote} testId="quote-edit-test-badge" />}
                {editingQuote.id && <NexusBanner doc={editingQuote} testId="quote-nexus-banner" />}
                {isFullAdmin(authUser) && editingQuote.id ? (
                  <TaxControls kind="quotes" doc={editingQuote} token={token}
                    onChange={(r) => { setEditingQuote((q) => ({ ...q, ...r })); fetchQuotes(); }} />
                ) : editingQuote.id && <TaxReadOnly doc={editingQuote} />}
                {editingQuote.id && <DepositControls quoteId={editingQuote.id} token={token} canEdit={isFullAdmin(authUser)} />}

                {/* Totals */}
                <div className="bg-neutral-50 rounded-lg p-4">
                  <div className="flex justify-end space-y-1">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Subtotal</span>
                        <span className="font-mono">{formatPrice(editingQuote.subtotal || 0)}</span>
                      </div>
                      <TaxTotals subtotal={editingQuote.subtotal} preview={editQuoteTax} formatPrice={formatPrice} testId="edit-quote-tax" />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={saveQuoteEdit} disabled={creating} className="btn-primary">
                    {creating ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuotesTab;
